import Debug from 'debug';
import { EventHandlerContext } from './types';
import { FeedItem } from '@/classes';

const debug = Debug('livefire:organism:feed'); // eslint-disable-line new-cap

/**
 * Generate a stable id based off the given feed item.
 * @param feedItem Livefire feed item that has at least the type
 * property and id property.
 * @returns A stable id suitable for use as a unique key identifying the item.
 */
export function generateCardId(feedItem: FeedItem) {
  return `${feedItem.type}-${feedItem.id}`;
}

export const MAX_FEED_ITEMS = 200;

/**
 * Limit the feed items to 200 items total.
 */
export function limitFeedItems(feedItems: FeedItem[]) {
  const maxItems = MAX_FEED_ITEMS;
  const count = feedItems.length;
  if (count < maxItems) {
    return [...feedItems];
  }
  debug(`Limited feed items to ${maxItems}`);
  return feedItems.slice(0, maxItems);
}

/**
 * Handles the newFeedItem event from the API controller.
 */
export function newFeedItem(
  { setFeedItems }: { setFeedItems: React.Dispatch<React.SetStateAction<FeedItem[]>> },
  evt: CustomEvent,
) {
  debug('got new feed item');

  setFeedItems((currentItems: FeedItem[]) => {
    // Create a copy since useState uses Object.is to determine updates, so a new array it is!
    const feedItems = [...currentItems];

    const item = evt.detail.feedItem;

    const cardId = generateCardId(item);
    const idx = feedItems.findIndex((fi) => cardId === generateCardId(fi));

    let feedItem = item;

    if (idx >= 0) {
      debug('updating existing item', idx);

      feedItem = feedItems[idx];
      feedItem.subject = item.subject;
      feedItem.eventTS = item.eventTS;
      feedItem.sentTS = item.sentTS;
      feedItem.count = item.count;
      feedItem.recipientName = item.recipientName;
      feedItem.percentWatched = item.percentWatched;
      feedItem.maxVideoWatchedPercent = item.maxVideoWatchedPercent;
      feedItem.commentMessage = item.commentMessage;

      // reset the timer tick so the time can update properly.
      feedItem.updateTimerTick = 0;

      feedItems.splice(idx, 1);
    } else {
      // check for a sent item with the same id so we can replace it.
      const sentIdx = feedItems.findIndex((fi) => item.id === fi.id && fi.type === 'sent');
      if (sentIdx >= 0) {
        feedItems.splice(sentIdx, 1);
      }
    }

    feedItems.unshift(feedItem);

    return limitFeedItems(feedItems);
  });
}

/**
 * Display a notification that an internet connection was lost.
 */
export function connectError(
  { setNotification, createNotification }: EventHandlerContext,
  evt: CustomEvent,
) {
  debug('got connect error?', evt);

  setNotification(
    createNotification({
      text: 'Internet Connection Lost.',
      type: 'warning',
    }),
  );
}

/**
 * Livefire established the socket connection.
 */
export function connectEvent(
  { setNotification, createNotification }: EventHandlerContext,
  evt: CustomEvent,
) {
  // turn off any notifications
  setNotification(createNotification());
}

/**
 * Reconnecting to LF has failed completely, socket will not try anymore.
 */
export function reconnectFailed(
  { setNotification, createNotification, api }: EventHandlerContext,
  evt: CustomEvent,
) {
  debug('reconnect failed, show retry', evt);

  function retry() {
    api.disconnect();
    api.connect();
    setNotification(
      createNotification({
        text: 'Retrying...',
      }),
    );
  }

  setNotification(
    createNotification({
      text: 'Internet Connection Lost.',
      type: 'warning',
      actionText: 'Retry',
      actionHandler: retry,
    }),
  );
}

/**
 * Livefire reconnected to sockets.
 */
export function reconnectEvent(
  { setNotification, createNotification }: EventHandlerContext,
  evt: CustomEvent,
) {
  debug('reconnected', evt);
  // turn off any notifications
  setNotification(createNotification());
}
